/**
 * Error Fallback UI Component
 * Bulletproof React pattern for error boundary fallback
 * Task: 11.3 - Stage 1: Error Boundary Consolidation
 *
 * User-friendly error display with recovery options.
 * Shows different levels of detail based on environment (dev vs prod).
 */

import { ErrorInfo } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  resetError: () => void;
  reloadPage: () => void;
}

/**
 * Error Fallback Component
 * Displays user-friendly error message with recovery options
 */
export function ErrorFallback({
  error,
  errorInfo,
  resetError,
  reloadPage,
}: ErrorFallbackProps) {
  const isDevelopment = import.meta.env.DEV;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-destructive">
            <AlertTriangle className="size-6" />
            <span>Something went wrong</span>
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* User-friendly message */}
          <div className="space-y-2">
            <p className="text-muted-foreground">
              We encountered an unexpected error. This has been logged and we'll look into it.
            </p>
            <p className="text-sm text-muted-foreground">
              You can try refreshing the page or return to the home page to continue.
            </p>
          </div>

          {/* Development-only error details */}
          {isDevelopment && error && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Error Details (Development Only)</h3>
              <div className="space-y-3 rounded-md bg-muted p-4">
                {/* Error message */}
                <div>
                  <p className="mb-1 text-xs font-semibold text-muted-foreground">Message:</p>
                  <pre className="overflow-auto whitespace-pre-wrap break-all text-xs text-destructive">
                    {error.message}
                  </pre>
                </div>

                {/* Error stack trace */}
                {error.stack && (
                  <details className="cursor-pointer">
                    <summary className="text-xs font-semibold text-muted-foreground">
                      Stack Trace
                    </summary>
                    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">
                      {error.stack}
                    </pre>
                  </details>
                )}

                {/* Component stack */}
                {errorInfo?.componentStack && (
                  <details className="cursor-pointer">
                    <summary className="text-xs font-semibold text-muted-foreground">
                      Component Stack
                    </summary>
                    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">
                      {errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          )}

          {/* Recovery actions */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              onClick={resetError}
              variant="default"
              className="flex items-center gap-2"
            >
              <RefreshCw className="size-4" />
              Try Again
            </Button>

            <Button
              onClick={reloadPage}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="size-4" />
              Reload Page
            </Button>

            <Button
              onClick={() => window.location.href = '/'}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Home className="size-4" />
              Go to Home
            </Button>
          </div>

          {/* Support information */}
          <div className="border-t pt-4">
            <p className="text-xs text-muted-foreground">
              If this problem persists, please contact support with the error details above.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Route Error Fallback - for use with React Router's errorElement
 * Uses useRouteError() to access the error thrown during route rendering
 */
export function RouteErrorFallback() {
  const error = useRouteError();

  const errorMessage = isRouteErrorResponse(error)
    ? error.statusText
    : error instanceof Error
      ? error.message
      : 'An unexpected error occurred';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4" role="alert">
      <Card className="w-full max-w-md border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-destructive">
            <AlertTriangle className="size-6" />
            Something went wrong
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">{errorMessage}</p>
          <div className="flex gap-3">
            <Button onClick={() => window.location.reload()}>
              <RefreshCw className="mr-2 size-4" />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => window.location.assign('/')}>
              <Home className="mr-2 size-4" />
              Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
